import { z } from 'zod';

/**
 * A conversation's own changes from the sidebar's menu (D22): `PATCH /api/conversations/:id`
 * renames it, archives it (its finished children with it) or brings it back from the archive.
 * `DELETE` deletes it for good.
 */
export const conversationChangeRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(140).optional(),
    archived: z.boolean().optional(),
  })
  .strict()
  .refine((body) => body.title !== undefined || body.archived !== undefined, {
    message: 'Nothing to change.',
  });
export type ConversationChangeRequest = z.infer<typeof conversationChangeRequestSchema>;

export const conversationChangeResponseSchema = z.object({
  conversation: z.uuid(),
  title: z.string(),
  archived: z.boolean(),
});
export type ConversationChangeResponse = z.infer<typeof conversationChangeResponseSchema>;

/** An archived conversation, found again with ⌘K (`GET /api/conversations/archived`). */
export const archivedConversationSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  updatedAt: z.string(),
  /** It lives on the linked Crowdex Cloud. */
  onCloud: z.boolean().optional(),
});
export type ArchivedConversation = z.infer<typeof archivedConversationSchema>;

/**
 * A conversation whose messages say what was searched (`GET /api/conversations/search?q=`), with
 * the words around it.
 */
export const conversationSearchHitSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  updatedAt: z.string(),
  /** "…le cache du store pnpm fait gagner…". */
  snippet: z.string(),
});
export type ConversationSearchHit = z.infer<typeof conversationSearchHitSchema>;

export const conversationSearchResponseSchema = z.object({
  conversations: z.array(conversationSearchHitSchema),
});
export type ConversationSearchResponse = z.infer<typeof conversationSearchResponseSchema>;

export const archivedConversationsResponseSchema = z.object({
  conversations: z.array(archivedConversationSchema),
});
export type ArchivedConversationsResponse = z.infer<typeof archivedConversationsResponseSchema>;

/**
 * `PUT /api/conversations/:id`: saves a conversation nothing was sent in yet, where it will run, so
 * what is opened in it (an app's tab, which belongs to the conversation shown) works in its
 * workspace before its first message. A conversation already saved is left as it is.
 */
export const saveDraftConversationRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(140),
    runsIn: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('this-computer'),
          /** The folder chosen in the composer, as it is named; none: the default workspace. */
          folder: z
            .object({ root: z.string().min(1).max(4096), name: z.string().min(1).max(80) })
            .strict()
            .nullable(),
          runtime: z.enum(['local', 'worktree']),
          /** The agent chosen (its registry id); none: the first installed one. */
          agent: z.string().min(1).max(200).nullable(),
        })
        .strict(),
      z.object({ kind: z.literal('cloud'), agent: z.string().min(1).max(200).nullable() }).strict(),
    ]),
  })
  .strict();
export type SaveDraftConversationRequest = z.infer<typeof saveDraftConversationRequestSchema>;

export const saveDraftConversationResponseSchema = z.object({
  conversation: z.uuid(),
  workspaceId: z.string(),
  logicalWorkspaceId: z.string().nullable(),
  title: z.string(),
  runtime: z.enum(['local', 'worktree', 'cloud']),
  /** It was saved by this request (false: it already was). */
  created: z.boolean(),
});
export type SaveDraftConversationResponse = z.infer<typeof saveDraftConversationResponseSchema>;
